import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import { getAttributionConversionReport, convertFirebaseTimestamp } from '../../services/funnelService';

const Container = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  box-shadow: ${theme.shadows.sm};
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
  flex-wrap: wrap;
`;

const HeaderText = styled.div`
  flex: 1;
  min-width: 220px;
`;

const SectionTitle = styled.h2`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h2};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.sm} 0;
`;

const Subtitle = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
  margin: 0;
`;

const Controls = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  align-items: center;
  flex-wrap: wrap;
`;

const Select = styled.select`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.text};
  border: 2px solid ${theme.colors.doryLighterBlue};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: white;
  cursor: pointer;
`;

const FilterChip = styled.button`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  font-weight: ${theme.typography.weights.medium};
  color: ${props => (props.$active ? 'white' : theme.colors.text)};
  background: ${props => (props.$active ? theme.colors.primary : 'white')};
  border: 2px solid ${theme.colors.primary};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  cursor: pointer;
`;

const Button = styled.button`
  background: ${props =>
    props.$variant === 'primary'
      ? `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.accent} 100%)`
      : 'white'};
  color: ${props => (props.$variant === 'primary' ? 'white' : theme.colors.text)};
  border: ${props =>
    props.$variant === 'primary' ? 'none' : `2px solid ${theme.colors.primary}`};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  font-weight: ${theme.typography.weights.medium};
  cursor: pointer;
  transition: all ${theme.animation.durations.fast};
  white-space: nowrap;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.md};
  }

  &:disabled {
    background: ${theme.colors.disabled};
    border-color: ${theme.colors.disabled};
    color: white;
    cursor: not-allowed;
    transform: none;
  }
`;

const ErrorMessage = styled.div`
  background: ${theme.colors.error}20;
  border: 1px solid ${theme.colors.error};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
  color: ${theme.colors.error};
  font-family: ${theme.typography.bodyFont};
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: ${theme.spacing.xl};
  color: ${theme.colors.textLight};
  font-family: ${theme.typography.bodyFont};
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: ${theme.spacing.lg};
  color: ${theme.colors.textLight};
  font-family: ${theme.typography.bodyFont};
`;

const SummaryStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.xl};
`;

const StatCard = styled.div`
  background: ${theme.colors.doryBeige};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.lg};
  text-align: center;
`;

const StatValue = styled.div`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h2};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.primary};
  margin-bottom: ${theme.spacing.xs};
`;

const StatLabel = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${theme.colors.textLight};
`;

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
`;

const Th = styled.th`
  text-align: left;
  padding: ${theme.spacing.md};
  border-bottom: 2px solid ${theme.colors.doryLighterBlue};
  color: ${theme.colors.textLight};
  font-weight: ${theme.typography.weights.medium};
  font-size: ${theme.typography.sizes.small};
  white-space: nowrap;
`;

const Tr = styled.tr`
  cursor: pointer;
  background: ${props => (props.$expanded ? `${theme.colors.primary}08` : 'transparent')};
  border-bottom: 1px solid ${theme.colors.doryLighterBlue};

  &:hover {
    background: ${theme.colors.neutral}10;
  }
`;

const Td = styled.td`
  padding: ${theme.spacing.md};
  color: ${theme.colors.text};
  vertical-align: top;
`;

const DetailRow = styled.tr`
  background: ${theme.colors.doryBeige};
`;

const DetailCell = styled.td`
  padding: ${theme.spacing.lg};
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing.lg};

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const DetailBlock = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.md};
  border: 1px solid ${theme.colors.doryLighterBlue};
`;

const DetailTitle = styled.h4`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.body};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.sm} 0;
`;

const MetaList = styled.dl`
  margin: 0;
  display: grid;
  grid-template-columns: auto 1fr;
  gap: ${theme.spacing.xs} ${theme.spacing.md};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
`;

const MetaKey = styled.dt`
  color: ${theme.colors.textLight};
  font-weight: ${theme.typography.weights.medium};
`;

const MetaValue = styled.dd`
  margin: 0;
  color: ${theme.colors.text};
  word-break: break-all;
`;

const Mono = styled.span`
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: ${theme.typography.sizes.small};
`;

const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.xs};
`;

const Badge = styled.span`
  display: inline-block;
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.tiny};
  font-weight: ${theme.typography.weights.medium};
  letter-spacing: ${theme.typography.letterSpacing.wide};
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: ${theme.borderRadius.sm};
  background: ${props => {
    if (props.$tone === 'success') return `${theme.colors.success}22`;
    if (props.$tone === 'error') return `${theme.colors.error}22`;
    if (props.$tone === 'warn') return `${theme.colors.warning}33`;
    return `${theme.colors.primary}18`;
  }};
  color: ${props => {
    if (props.$tone === 'success') return theme.colors.success;
    if (props.$tone === 'error') return theme.colors.error;
    if (props.$tone === 'warn') return theme.colors.textLight;
    return theme.colors.primary;
  }};
  white-space: nowrap;
`;

const NestedTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${theme.typography.sizes.small};
  margin-top: ${theme.spacing.sm};
`;

const NestedTh = styled.th`
  text-align: left;
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-bottom: 1px solid ${theme.colors.doryLighterBlue};
  color: ${theme.colors.textLight};
  font-weight: ${theme.typography.weights.medium};
`;

const NestedTd = styled.td`
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-bottom: 1px solid ${theme.colors.doryLighterBlue}88;
  vertical-align: top;
  color: ${theme.colors.text};
`;

const CountBadge = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${theme.colors.textLight};
  margin-bottom: ${theme.spacing.sm};
`;

const DAY_OPTIONS = [7, 14, 30, 60, 90];

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = convertFirebaseTimestamp(value) || (value instanceof Date ? value : new Date(value));
  if (!date || isNaN(date.getTime())) return '—';
  return date.toLocaleString();
};

const truncate = (value, max = 28) => {
  if (!value) return '—';
  const str = String(value);
  return str.length > max ? `${str.slice(0, max)}…` : str;
};

const platformStatusBadge = (result) => {
  if (!result) return <Badge $tone="warn">n/a</Badge>;
  if (result.sent) return <Badge $tone="success">sent</Badge>;
  if (result.validated) return <Badge $tone="success">validated</Badge>;
  if (result.skipped) {
    return <Badge $tone="warn">skipped{result.reason ? `: ${result.reason}` : ''}</Badge>;
  }
  if (result.error) return <Badge $tone="error">error</Badge>;
  return <Badge $tone="warn">unknown</Badge>;
};

const AttributionConversionReportScreen = () => {
  const [days, setDays] = useState(30);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedShortcode, setExpandedShortcode] = useState(null);
  const [filter, setFilter] = useState('all'); // all | attributed | converted

  const loadReport = useCallback(async (dayWindow = days) => {
    setLoading(true);
    setError('');
    try {
      const data = await getAttributionConversionReport(dayWindow);
      setReport(data);
      setExpandedShortcode(null);
    } catch (err) {
      console.error('Failed to load attribution/conversion report:', err);
      setError(err.message || 'Failed to load report');
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    loadReport(days);
  }, [days, loadReport]);

  const filteredUsers = useMemo(() => {
    const users = report?.users || [];
    if (filter === 'attributed') {
      return users.filter((u) => (u.attributionPlatforms || []).length > 0);
    }
    if (filter === 'converted') {
      return users.filter((u) => (u.conversionSummary?.anySent || 0) > 0);
    }
    return users;
  }, [report, filter]);

  const summary = report?.summary;

  return (
    <Container>
      <SectionHeader>
        <HeaderText>
          <SectionTitle>Attribution & Conversions</SectionTitle>
          <Subtitle>
            Recent users with ad attribution (`currentAttribution`) and platform
            conversion sends (`conversionEventsSent`). Localhost sessions excluded.
          </Subtitle>
        </HeaderText>
        <Controls>
          <Select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            disabled={loading}
            aria-label="Date range"
          >
            {DAY_OPTIONS.map((option) => (
              <option key={option} value={option}>
                Last {option} days
              </option>
            ))}
          </Select>
          <FilterChip
            type="button"
            $active={filter === 'all'}
            onClick={() => setFilter('all')}
          >
            All
          </FilterChip>
          <FilterChip
            type="button"
            $active={filter === 'attributed'}
            onClick={() => setFilter('attributed')}
          >
            With attribution
          </FilterChip>
          <FilterChip
            type="button"
            $active={filter === 'converted'}
            onClick={() => setFilter('converted')}
          >
            With conversions
          </FilterChip>
          <Button onClick={() => loadReport(days)} disabled={loading}>
            Refresh
          </Button>
        </Controls>
      </SectionHeader>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      {loading ? (
        <LoadingMessage>Loading attribution & conversion report…</LoadingMessage>
      ) : !report ? (
        <EmptyMessage>No report data available.</EmptyMessage>
      ) : (
        <>
          <SummaryStats>
            <StatCard>
              <StatValue>{summary.totalUsersInWindow}</StatValue>
              <StatLabel>Users (last {days}d)</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>{summary.withAttribution}</StatValue>
              <StatLabel>With attribution</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>{summary.withConversions}</StatValue>
              <StatLabel>With conversions sent</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>{summary.platformCounts.meta}</StatValue>
              <StatLabel>Meta click IDs</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>{summary.platformCounts.google}</StatValue>
              <StatLabel>Google click IDs</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>{summary.platformCounts.linkedin}</StatValue>
              <StatLabel>LinkedIn click IDs</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>{summary.conversionSentCounts.meta}</StatValue>
              <StatLabel>Meta events sent</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>{summary.conversionSentCounts.google}</StatValue>
              <StatLabel>Google events sent</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>{summary.conversionSentCounts.linkedin}</StatValue>
              <StatLabel>LinkedIn events sent</StatLabel>
            </StatCard>
          </SummaryStats>

          <CountBadge>
            Showing {filteredUsers.length} of {report.users.length} users
            {report.cutoff
              ? ` · since ${report.cutoff.toLocaleDateString()}`
              : ''}
          </CountBadge>

          {filteredUsers.length === 0 ? (
            <EmptyMessage>No users match this filter in the selected window.</EmptyMessage>
          ) : (
            <TableWrapper>
              <Table>
                <thead>
                  <tr>
                    <Th />
                    <Th>Shortcode</Th>
                    <Th>Started</Th>
                    <Th>User</Th>
                    <Th>Source / campaign</Th>
                    <Th>Click IDs</Th>
                    <Th>Conversions</Th>
                    <Th>Progress</Th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => {
                    const expanded = expandedShortcode === user.shortcode;
                    const attr = user.attribution || {};
                    return (
                      <React.Fragment key={user.shortcode}>
                        <Tr
                          $expanded={expanded}
                          onClick={() =>
                            setExpandedShortcode(expanded ? null : user.shortcode)
                          }
                        >
                          <Td>{expanded ? '▾' : '▸'}</Td>
                          <Td>
                            <Mono>{user.shortcode}</Mono>
                          </Td>
                          <Td>{formatDateTime(user.startTime)}</Td>
                          <Td>
                            <div>{user.name || '—'}</div>
                            <Mono>{user.email || '—'}</Mono>
                          </Td>
                          <Td>
                            <div>{attr.utm_source || '—'}</div>
                            <Mono>
                              {[attr.utm_medium, attr.utm_campaign]
                                .filter(Boolean)
                                .join(' / ') || '—'}
                            </Mono>
                          </Td>
                          <Td>
                            <BadgeRow>
                              {user.attributionPlatforms.length === 0 && (
                                <Badge $tone="warn">none</Badge>
                              )}
                              {user.attributionPlatforms.map((platform) => (
                                <Badge key={platform}>{platform}</Badge>
                              ))}
                            </BadgeRow>
                          </Td>
                          <Td>
                            {user.conversionEvents.length === 0 ? (
                              <Badge $tone="warn">none</Badge>
                            ) : (
                              <BadgeRow>
                                <Badge>
                                  {user.conversionEvents.length} attempt
                                  {user.conversionEvents.length === 1 ? '' : 's'}
                                </Badge>
                                {user.conversionSummary.metaSent > 0 && (
                                  <Badge $tone="success">
                                    meta {user.conversionSummary.metaSent}
                                  </Badge>
                                )}
                                {user.conversionSummary.googleSent > 0 && (
                                  <Badge $tone="success">
                                    google {user.conversionSummary.googleSent}
                                  </Badge>
                                )}
                                {user.conversionSummary.linkedinSent > 0 && (
                                  <Badge $tone="success">
                                    linkedin {user.conversionSummary.linkedinSent}
                                  </Badge>
                                )}
                                {user.conversionSummary.errors > 0 && (
                                  <Badge $tone="error">
                                    {user.conversionSummary.errors} err
                                  </Badge>
                                )}
                              </BadgeRow>
                            )}
                          </Td>
                          <Td>
                            <Mono>{user.completedScreensCount}</Mono>
                            {user.completedFunnel ? ' · done' : ''}
                          </Td>
                        </Tr>
                        {expanded && (
                          <DetailRow>
                            <DetailCell colSpan={8}>
                              <DetailGrid>
                                <DetailBlock>
                                  <DetailTitle>currentAttribution</DetailTitle>
                                  {!user.attribution ? (
                                    <EmptyMessage>No attribution on this user.</EmptyMessage>
                                  ) : (
                                    <MetaList>
                                      <MetaKey>capturedAt</MetaKey>
                                      <MetaValue>
                                        {formatDateTime(attr.capturedAt)}
                                      </MetaValue>
                                      <MetaKey>landingPage</MetaKey>
                                      <MetaValue>{attr.landingPage || '—'}</MetaValue>
                                      <MetaKey>utm_source</MetaKey>
                                      <MetaValue>{attr.utm_source || '—'}</MetaValue>
                                      <MetaKey>utm_medium</MetaKey>
                                      <MetaValue>{attr.utm_medium || '—'}</MetaValue>
                                      <MetaKey>utm_campaign</MetaKey>
                                      <MetaValue>{attr.utm_campaign || '—'}</MetaValue>
                                      <MetaKey>utm_content</MetaKey>
                                      <MetaValue>{attr.utm_content || '—'}</MetaValue>
                                      <MetaKey>utm_term</MetaKey>
                                      <MetaValue>{attr.utm_term || '—'}</MetaValue>
                                      <MetaKey>fbclid</MetaKey>
                                      <MetaValue>
                                        <Mono>{truncate(attr.fbclid, 48)}</Mono>
                                      </MetaValue>
                                      <MetaKey>gclid</MetaKey>
                                      <MetaValue>
                                        <Mono>{truncate(attr.gclid, 48)}</Mono>
                                      </MetaValue>
                                      <MetaKey>gbraid</MetaKey>
                                      <MetaValue>
                                        <Mono>{truncate(attr.gbraid, 48)}</Mono>
                                      </MetaValue>
                                      <MetaKey>wbraid</MetaKey>
                                      <MetaValue>
                                        <Mono>{truncate(attr.wbraid, 48)}</Mono>
                                      </MetaValue>
                                      <MetaKey>li_fat_id</MetaKey>
                                      <MetaValue>
                                        <Mono>{truncate(attr.li_fat_id, 48)}</Mono>
                                      </MetaValue>
                                    </MetaList>
                                  )}
                                </DetailBlock>

                                <DetailBlock>
                                  <DetailTitle>User & session</DetailTitle>
                                  <MetaList>
                                    <MetaKey>name</MetaKey>
                                    <MetaValue>{user.name || '—'}</MetaValue>
                                    <MetaKey>email</MetaKey>
                                    <MetaValue>{user.email || '—'}</MetaValue>
                                    <MetaKey>phone</MetaKey>
                                    <MetaValue>{user.phone || '—'}</MetaValue>
                                    <MetaKey>hostname</MetaKey>
                                    <MetaValue>{user.hostname || '—'}</MetaValue>
                                    <MetaKey>lastActive</MetaKey>
                                    <MetaValue>
                                      {formatDateTime(user.lastActive)}
                                    </MetaValue>
                                    <MetaKey>currentScreen</MetaKey>
                                    <MetaValue>{user.currentScreen || '—'}</MetaValue>
                                    <MetaKey>completedScreens</MetaKey>
                                    <MetaValue>{user.completedScreensCount}</MetaValue>
                                    <MetaKey>clickEvents</MetaKey>
                                    <MetaValue>{user.clickEventsCount}</MetaValue>
                                  </MetaList>
                                </DetailBlock>

                                <DetailBlock style={{ gridColumn: '1 / -1' }}>
                                  <DetailTitle>
                                    Recent clickEvents (last {user.clickEvents.length}
                                    {user.clickEventsCount > user.clickEvents.length
                                      ? ` of ${user.clickEventsCount}`
                                      : ''}
                                    )
                                  </DetailTitle>
                                  {user.clickEvents.length === 0 ? (
                                    <EmptyMessage>
                                      No click events recorded for this user.
                                    </EmptyMessage>
                                  ) : (
                                    <NestedTable>
                                      <thead>
                                        <tr>
                                          <NestedTh>Time</NestedTh>
                                          <NestedTh>Platform</NestedTh>
                                          <NestedTh>Click ID</NestedTh>
                                          <NestedTh>UTM</NestedTh>
                                          <NestedTh>Landing</NestedTh>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {[...user.clickEvents]
                                          .reverse()
                                          .map((click, index) => (
                                            <tr key={`${click.clickId || 'click'}-${index}`}>
                                              <NestedTd>
                                                {formatDateTime(click.timestamp)}
                                              </NestedTd>
                                              <NestedTd>
                                                <Badge>{click.platform || 'unknown'}</Badge>
                                              </NestedTd>
                                              <NestedTd>
                                                <Mono>
                                                  {truncate(click.clickId, 36)}
                                                </Mono>
                                              </NestedTd>
                                              <NestedTd>
                                                {[
                                                  click.utm_source,
                                                  click.utm_medium,
                                                  click.utm_campaign
                                                ]
                                                  .filter(Boolean)
                                                  .join(' / ') || '—'}
                                              </NestedTd>
                                              <NestedTd>
                                                {truncate(click.landingPage, 48)}
                                              </NestedTd>
                                            </tr>
                                          ))}
                                      </tbody>
                                    </NestedTable>
                                  )}
                                </DetailBlock>

                                <DetailBlock style={{ gridColumn: '1 / -1' }}>
                                  <DetailTitle>
                                    completedScreens ({user.completedScreensCount})
                                  </DetailTitle>
                                  {(user.completedScreens || []).length === 0 ? (
                                    <EmptyMessage>
                                      No completed screens recorded for this user.
                                    </EmptyMessage>
                                  ) : (
                                    <NestedTable>
                                      <thead>
                                        <tr>
                                          <NestedTh>#</NestedTh>
                                          <NestedTh>Screen key</NestedTh>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {user.completedScreens.map((screenKey, index) => (
                                          <tr key={`${screenKey}-${index}`}>
                                            <NestedTd>{index + 1}</NestedTd>
                                            <NestedTd>
                                              <Mono>{screenKey || '—'}</Mono>
                                            </NestedTd>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </NestedTable>
                                  )}
                                </DetailBlock>

                                <DetailBlock style={{ gridColumn: '1 / -1' }}>
                                  <DetailTitle>conversionEventsSent</DetailTitle>
                                  {user.conversionEvents.length === 0 ? (
                                    <EmptyMessage>
                                      No conversion events recorded for this user.
                                    </EmptyMessage>
                                  ) : (
                                    <NestedTable>
                                      <thead>
                                        <tr>
                                          <NestedTh>Sent at</NestedTh>
                                          <NestedTh>Item</NestedTh>
                                          <NestedTh>Event ID</NestedTh>
                                          <NestedTh>Meta</NestedTh>
                                          <NestedTh>Google</NestedTh>
                                          <NestedTh>LinkedIn</NestedTh>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {[...user.conversionEvents]
                                          .sort((a, b) => {
                                            const aTime =
                                              convertFirebaseTimestamp(a?.sentAt)?.getTime() || 0;
                                            const bTime =
                                              convertFirebaseTimestamp(b?.sentAt)?.getTime() || 0;
                                            return bTime - aTime;
                                          })
                                          .map((event, index) => {
                                            const results = event.results || {};
                                            return (
                                              <tr key={event.eventId || index}>
                                                <NestedTd>
                                                  {formatDateTime(event.sentAt)}
                                                </NestedTd>
                                                <NestedTd>
                                                  <Mono>
                                                    {event.itemType || '?'}:
                                                    {event.itemKey || '—'}
                                                  </Mono>
                                                </NestedTd>
                                                <NestedTd>
                                                  <Mono>
                                                    {truncate(event.eventId, 36)}
                                                  </Mono>
                                                </NestedTd>
                                                <NestedTd>
                                                  {platformStatusBadge(results.meta)}
                                                  {results.meta?.error && (
                                                    <div>
                                                      <Mono>{results.meta.error}</Mono>
                                                    </div>
                                                  )}
                                                </NestedTd>
                                                <NestedTd>
                                                  {platformStatusBadge(results.google)}
                                                  {results.google?.error && (
                                                    <div>
                                                      <Mono>{results.google.error}</Mono>
                                                    </div>
                                                  )}
                                                </NestedTd>
                                                <NestedTd>
                                                  {platformStatusBadge(results.linkedin)}
                                                  {results.linkedin?.error && (
                                                    <div>
                                                      <Mono>
                                                        {results.linkedin.error}
                                                      </Mono>
                                                    </div>
                                                  )}
                                                </NestedTd>
                                              </tr>
                                            );
                                          })}
                                      </tbody>
                                    </NestedTable>
                                  )}
                                </DetailBlock>
                              </DetailGrid>
                            </DetailCell>
                          </DetailRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </Table>
            </TableWrapper>
          )}
        </>
      )}
    </Container>
  );
};

export default AttributionConversionReportScreen;
